import React, { useState, useEffect } from 'react';
import { Container, Grid, Box, Typography, Button, Chip, Card, Fade } from '@mui/material';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '150+', label: 'Projects Delivered' },
    { number: '50+', label: 'Happy Clients' },
  ];

  return (
    <Box
      id="home"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 15, md: 20 },
        pb: { xs: 10, md: 15 },
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Fade in={visible} timeout={1000}>
              <Box>
                <Chip
                  label="10 Years of Excellence"
                  sx={{
                    mb: 3,
                    px: 2,
                    py: 0.5,
                    background: 'rgba(0, 245, 255, 0.1)',
                    border: '1px solid rgba(0, 245, 255, 0.3)',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    animation: 'fadeInUp 1s ease-out 0.2s both',
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
                    lineHeight: 1.1,
                    mb: 3,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #B0B8C1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'fadeInUp 1s ease-out 0.4s both',
                  }}
                >
                  Building Digital Excellence
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.secondary',
                    mb: 5,
                    maxWidth: '650px',
                    lineHeight: 1.8,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    animation: 'fadeInUp 1s ease-out 0.6s both',
                  }}
                >
                  Senior Fullstack Developer & DevOps Expert specializing in Web & Mobile Applications,
                  CI/CD, Enterprise PLM Solutions, and Desktop Applications.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    animation: 'fadeInUp 1s ease-out 0.8s both',
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() =>
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                    }
                    sx={{
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                      fontWeight: 700,
                      fontSize: '1rem',
                      boxShadow: '0 8px 32px rgba(0, 245, 255, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #00D9E6, #FF5722)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 40px rgba(0, 245, 255, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Get In Touch
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() =>
                      document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })
                    }
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 700,
                      fontSize: '1rem',
                      '&:hover': {
                        borderColor: 'primary.main',
                        background: 'rgba(0, 245, 255, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    View Projects
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>

          <Grid item xs={12} md={5}>
            <Fade in={visible} timeout={1000} style={{ transitionDelay: '400ms' }}>
              <Box>
                <Grid container spacing={3}>
                  {stats.map((stat, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Card
                        sx={{
                          background: 'var(--card-bg)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid var(--border-subtle)',
                          p: 3,
                          transition: 'all 0.4s ease',
                          '&:hover': {
                            transform: 'translateX(10px)',
                            borderColor: 'primary.main',
                            boxShadow: '0 8px 32px rgba(0, 245, 255, 0.2)',
                          },
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: '3rem',
                            fontWeight: 900,
                            background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                          }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 600,
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
